import React, { useState, useEffect } from "react";
import { Loader, Sparkles, X } from "lucide-react";
import posthog from "../lib/posthogClient";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../providers/AuthProvider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useAddVehicleNote, useUpdateVehicleNote } from "../hooks/useVehicleData";
import { useDeleteEntityAttachment } from "../hooks/useEntityAttachments";
import { useAIPhotoDescription, isImageFile } from "../hooks/useAIPhotoDescription";
import { uploadManager } from "../services/uploadManager";
import { FilePickerZone } from "./FilePickerZone";
import { toast } from "sonner";



interface ExistingAttachment {
  photo_id: string;
  url: string;
  file_name?: string;
  thumb_url?: string | null;
}

interface VehicleNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleInventoryId: string;
  onSuccess?: () => void;
  mode: "create" | "edit";
  existingNote?: {
    note_id: string;
    note: string;
    attachments: ExistingAttachment[];
  };
}

export const VehicleNoteModal: React.FC<VehicleNoteModalProps> = ({
  open,
  onOpenChange,
  vehicleInventoryId,
  onSuccess,
  mode,
  existingNote,
}) => {
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<string[]>([]);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  const queryClient = useQueryClient();
  const addNoteMutation = useAddVehicleNote();
  const updateNoteMutation = useUpdateVehicleNote();
  const deleteAttachmentMutation = useDeleteEntityAttachment("note");
  const { generateDescription } = useAIPhotoDescription();
  const { user } = useAuth();
  console.log("user", user)

  const handleGenerateDescription = () => {
    const images = selectedFiles.filter(isImageFile);
    if (images.length === 0) return;

    setIsGeneratingDescription(true);
    generateDescription(images)
      .then((description) => {
        setNoteText((prev) => {
          if (prev.trim()) return `${prev}\n\n${description}`;
          return description;
        });
        setIsGeneratingDescription(false);
      })
      .catch(() => {
        toast.error("Could not generate description. Please write it manually.");
        setIsGeneratingDescription(false);
      });
  };

  // Initialize/reset form when modal opens or mode changes
  useEffect(() => {
    if (open) {
      if (mode === "edit" && existingNote) {
        setNoteText(existingNote.note);
        setExistingAttachments(existingNote.attachments || []);
        setAttachmentsToDelete([]);
      } else {
        setNoteText("");
        setExistingAttachments([]);
        setAttachmentsToDelete([]);
      }
      setSelectedFiles([]);
    }
  }, [open, mode, existingNote]);

  const handleRemoveExistingAttachment = (photoId: string) => {
    setAttachmentsToDelete((prev) => [...prev, photoId]);
    setExistingAttachments((prev) => prev.filter((a) => a.photo_id !== photoId));
  };

  const handleSubmit = async () => {
    if (!noteText.trim()) {
      toast.error("Please enter a note.");
      return;
    }

    if (!user?.email) {
      toast.error("User email not found. Please log in again.");
      return;
    }

    try {
      let noteId: string | undefined;

      if (mode === "edit" && existingNote) {
        await updateNoteMutation.mutateAsync({
          noteId: existingNote.note_id,
          note: noteText,
          vehicleInventoryId,
          userEmail: user.email,
        });
        noteId = existingNote.note_id;

        if (attachmentsToDelete.length > 0) {
          for (const photoId of attachmentsToDelete) {
            try {
              await deleteAttachmentMutation.mutateAsync(photoId);
            } catch (error) {
              console.error(`Failed to delete attachment ${photoId}:`, error);
              posthog.capture("frontend_silent_failure", {
                context: "VehicleNoteModal.deleteAttachment",
                photo_id: photoId,
                error_message: (error as Error)?.message,
              });
            }
          }
        }
      } else {
        const noteResponse = await addNoteMutation.mutateAsync({
          vehicleInventoryId,
          note: noteText,
          userEmail: user.email,
        });
        noteId = noteResponse.note?.note_id;
      }

      if (selectedFiles.length > 0 && noteId) {
        uploadManager.queueUploads(
          selectedFiles,
          "note",
          noteId,
          user.email,
          () => {
            queryClient.invalidateQueries({ queryKey: ["entity-attachments", "note", noteId] });
          }
        );
      }

      if (mode === "edit" && existingNote && attachmentsToDelete.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["entity-attachments", "note", existingNote.note_id] });
      }

      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error(`Error ${mode === "edit" ? "updating" : "creating"} note:`, error);
      toast.error(`Failed to ${mode === "edit" ? "update" : "create"} note. Please try again.`);
    }
  };

  const isSubmitting = addNoteMutation.isPending || updateNoteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Edit Vehicle Note" : "Add Vehicle Note"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Note Text */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="note-text">Note *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateDescription}
                disabled={selectedFiles.filter(isImageFile).length === 0 || isGeneratingDescription}
                className="h-7 gap-1.5 text-xs"
              >
                {isGeneratingDescription ? (
                  <Loader className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {isGeneratingDescription ? "Generating…" : "AI Description"}
              </Button>
            </div>
            <Textarea
              id="note-text"
              placeholder="Enter your note here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Existing Attachments (Edit Mode Only) */}
          {mode === "edit" && existingAttachments.length > 0 && (
            <div className="space-y-2">
              <Label>Current Attachments</Label>
              <div className="space-y-2">
                {existingAttachments.map((attachment) => (
                  <div
                    key={attachment.photo_id}
                    className="flex items-center justify-between p-2 bg-muted rounded-md"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <img
                        src={attachment.thumb_url || attachment.url}
                        alt={attachment.file_name || "Attachment"}
                        className="h-10 w-10 object-cover rounded"
                      />
                      <span className="text-sm truncate">
                        {attachment.file_name || "Image"}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveExistingAttachment(attachment.photo_id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachment Upload Section */}
          <FilePickerZone
            files={selectedFiles}
            onChange={setSelectedFiles}
            inputId="note-file-input"
            label={mode === "edit" ? "Add New Attachments" : "Attachments (Optional)"}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isGeneratingDescription || !noteText.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                {mode === "edit" ? "Saving..." : "Creating..."}
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "Add Note"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Re-export with old name for backwards compatibility
export const AddVehicleNoteModal = VehicleNoteModal;
